/**
 * InsightsPage Component
 *
 * Main page for AI-powered insights with tabs for:
 * - Emission Reduction recommendations
 * - Facility Benchmarking
 * - Data Quality alerts
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  Stack,
  Card,
  CardContent,
  Skeleton,
  useTheme,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  EnergySavingsLeaf as EcoIcon,
  BarChart as BenchmarkIcon,
  FactCheck as QualityIcon,
  AutoAwesome as AIIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useApp } from '../../contexts/AuthContext';
import RecommendationCard from './RecommendationCard';
import DataQualityAlerts from './DataQualityAlerts';
import BenchmarkChart from './BenchmarkChart';
import {
  getRecommendations,
  getBenchmarks,
  getDataQualityAlerts,
  refreshInsights,
  getInsightStatus,
  RecommendationResponse,
  BenchmarkResponse,
  DataQualityResponse,
  InsightStatus,
  RecommendationCategory
} from '../../services/insightService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`insights-tabpanel-${index}`}
      aria-labelledby={`insights-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const InsightsPage: React.FC = () => {
  const theme = useTheme();
  const { user } = useApp();
  const isAdmin = user?.role === 'Admin';

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [recommendations, setRecommendations] = useState<RecommendationResponse | null>(null);
  const [benchmarks, setBenchmarks] = useState<BenchmarkResponse | null>(null);
  const [dataQuality, setDataQuality] = useState<DataQualityResponse | null>(null);
  const [status, setStatus] = useState<InsightStatus | null>(null);

  // Load all data
  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [recsData, benchData, qualityData, statusData] = await Promise.all([
        getRecommendations().catch((err) => { console.error('Failed to load recommendations:', err); return null; }),
        getBenchmarks().catch((err) => { console.error('Failed to load benchmarks:', err); return null; }),
        getDataQualityAlerts().catch((err) => { console.error('Failed to load data quality:', err); return null; }),
        getInsightStatus().catch((err) => { console.error('Failed to load insight status:', err); return null; }),
      ]);

      if (recsData) setRecommendations(recsData);
      if (benchData) setBenchmarks(benchData);
      if (qualityData) setDataQuality(qualityData);
      if (statusData) setStatus(statusData);

      // Show error only if all calls failed
      if (!recsData && !benchData && !qualityData && !statusData) {
        setError('AI insights are currently unavailable. This may be because the insights service is not configured.');
      }
    } catch (err: any) {
      console.error('Failed to load insights:', err);
      setError(err.message || 'Failed to load AI insights');
    } finally {
      setLoading(false);
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    if (!isAdmin) return;

    setRefreshing(true);
    try {
      const refreshedData = await refreshInsights();
      setRecommendations(refreshedData);

      // Reload other data too
      const [benchData, qualityData] = await Promise.all([
        getBenchmarks(),
        getDataQualityAlerts()
      ]);
      setBenchmarks(benchData);
      setDataQuality(qualityData);
    } catch (err: any) {
      console.error('Failed to refresh insights:', err);
      setError(err.message || 'Failed to refresh insights');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filter recommendations by category
  const emissionRecs = recommendations?.recommendations.filter(
    r => r.category === 'emissions'
  ) || [];

  const qualityRecs = recommendations?.recommendations.filter(
    r => r.category === 'data_quality'
  ) || [];

  // Count high priority items
  const highPriorityCount = recommendations?.recommendations.filter(
    r => r.priority === 'high'
  ).length || 0;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" alignItems="center" gap={2} mb={4}>
          <AIIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
          <Typography variant="h4" fontWeight={700}>
            AI-Powered Insights
          </Typography>
        </Box>

        <Stack spacing={3}>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
          ))}
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={4}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <AIIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
            <Typography variant="h4" fontWeight={700}>
              AI-Powered Insights
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Intelligent recommendations powered by Claude AI to help reduce your carbon footprint
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={2}>
          {status?.mock_mode && (
            <Chip
              label="Demo Mode"
              color="warning"
              size="small"
              icon={<InfoIcon />}
            />
          )}

          {recommendations?.cached && (
            <Tooltip title="Results are cached for performance">
              <Chip label="Cached" size="small" variant="outlined" />
            </Tooltip>
          )}

          {isAdmin && (
            <Button
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Stack direction="row" spacing={3} mb={4}>
        <Card sx={{ flex: 1, cursor: 'pointer' }} onClick={() => setActiveTab(0)}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: `${theme.palette.success.main}15`
                }}
              >
                <EcoIcon sx={{ color: theme.palette.success.main, fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={700}>
                  {emissionRecs.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Emission Reduction Tips
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, cursor: 'pointer' }} onClick={() => setActiveTab(1)}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: `${theme.palette.info.main}15`
                }}
              >
                <BenchmarkIcon sx={{ color: theme.palette.info.main, fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={700}>
                  {benchmarks?.facilities.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Facility Benchmarks
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, cursor: 'pointer' }} onClick={() => setActiveTab(2)}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: `${theme.palette.warning.main}15`
                }}
              >
                <QualityIcon sx={{ color: theme.palette.warning.main, fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={700}>
                  {dataQuality?.total_count || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Data Quality Alerts
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Stack>

      {/* High Priority Alert */}
      {highPriorityCount > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Action Required</AlertTitle>
          You have <strong>{highPriorityCount} high-priority</strong> recommendations
          that could significantly reduce your emissions.
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab
            icon={<EcoIcon />}
            iconPosition="start"
            label={`Emission Reduction (${emissionRecs.length})`}
          />
          <Tab
            icon={<BenchmarkIcon />}
            iconPosition="start"
            label={`Benchmarking (${benchmarks?.insights.length || 0})`}
          />
          <Tab
            icon={<QualityIcon />}
            iconPosition="start"
            label={`Data Quality (${dataQuality?.total_count || 0})`}
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Emission Reduction Tab */}
          <TabPanel value={activeTab} index={0}>
            {emissionRecs.length === 0 ? (
              <Alert severity="success">
                <AlertTitle>Great Job!</AlertTitle>
                No emission reduction recommendations at this time. Your organization
                is on track with its sustainability goals.
              </Alert>
            ) : (
              <Stack spacing={2}>
                {emissionRecs.map(rec => (
                  <RecommendationCard key={rec.id} recommendation={rec} />
                ))}
              </Stack>
            )}
          </TabPanel>

          {/* Benchmarking Tab */}
          <TabPanel value={activeTab} index={1}>
            {benchmarks ? (
              <BenchmarkChart data={benchmarks} />
            ) : (
              <Alert severity="info">
                No benchmark data available. Add more facilities and emission data to enable benchmarking.
              </Alert>
            )}
          </TabPanel>

          {/* Data Quality Tab */}
          <TabPanel value={activeTab} index={2}>
            {dataQuality ? (
              <DataQualityAlerts data={dataQuality} />
            ) : (
              <Alert severity="info">
                No data quality information available.
              </Alert>
            )}
          </TabPanel>
        </Box>
      </Paper>

      {/* Status Footer */}
      {status && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={3}
          px={2}
        >
          <Typography variant="caption" color="text.secondary">
            {status.mock_mode
              ? 'Running in demo mode. Connect Anthropic API for live insights.'
              : `API requests remaining: ${status.requests_remaining}/hour`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Generated: {recommendations?.generated_at
              ? new Date(recommendations.generated_at).toLocaleString()
              : 'N/A'}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default InsightsPage;
